/**
 * Calculated Field Helper
 * Handles calculated field formula evaluation
 */

export class CalculatedFieldHelper {
  static calculatedFields = new Map(); // fieldId -> { formula, decimalPoints }

  /**
   * Setup calculated field
   * @param {string} fieldId - Field ID
   * @param {Array} formula - Formula array
   * @param {number} decimalPoints - Decimal points
   */
  static setup(fieldId, formula, decimalPoints = 0) {
    if (!formula || !Array.isArray(formula) || formula.length === 0) {
      return;
    }

    // Store configuration for event delegation
    this.calculatedFields.set(fieldId, { formula, decimalPoints });

    // Initial calculation
    this.calculateField(fieldId);

    // Setup event delegation if not already set up
    if (!this.delegationSetup) {
      this.setupEventDelegation();
      this.delegationSetup = true;
    }
  }

  /**
   * Setup event delegation for all calculated fields
   */
  static setupEventDelegation() {
    const handler = (e) => {
      // Check if this field is part of any calculated field's formula
      const fieldName = e.target.name || e.target.id;
      if (!fieldName) return;

      // Find all calculated fields that depend on this field
      this.calculatedFields.forEach((config, calculatedFieldId) => {
        const dependsOnField = config.formula.some(term => 
          term.length === 2 && term[1] === fieldName
        );
        
        if (dependsOnField) {
          this.calculateField(calculatedFieldId);
        }
      });
    };

    document.addEventListener('input', handler, true);
    document.addEventListener('change', handler, true);
  }

  /**
   * Calculate field value
   * @param {string} fieldId - Calculated field ID
   */
  static calculateField(fieldId) {
    const config = this.calculatedFields.get(fieldId);
    if (!config) return;

    const { formula, decimalPoints } = config;
    const allowedOperators = ['+', '-', '*', '/', 'X'];
    
    let val = 0;
    let operator = false;

    for (let i = 0; i < formula.length; i++) {
      const fld = formula[i];

      if (fld.length === 1 && allowedOperators.includes(fld[0])) {
        operator = fld[0];
        continue;
      }

      let current = 0;
      if (fld.length === 1 && !allowedOperators.includes(fld[0])) {
        current = Number(fld[0]);
      } else if (fld.length === 2) {
        const inputElement = document.querySelector(`[name="${fld[1]}"]`);
        const inputVal = inputElement?.type === 'radio'
          ? document.querySelector(`[name="${fld[1]}"]:checked`)?.value || ''
          : inputElement?.value || '';
        current = Number(parseFloat(inputVal).toFixed(decimalPoints)) || 0;
      }

      if (operator) {
        switch (operator) {
          case '+':
            val = val + current;
            break;
          case '-':
            val = val - current;
            break;
          case 'X':
          case '*':
            val = val * current;
            break;
          case '/':
            if (current !== 0) val = val / current;
            break;
        }
      } else {
        val = current;
      }
    }

    if (!isNaN(val)) {
      const finalValue = parseFloat(val).toFixed(decimalPoints);
      const targetField = document.querySelector(`[name="${fieldId}"]`);
      if (targetField) {
        targetField.value = finalValue;
        targetField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }
}

